/**
 * The only fields grouping needs from a run. Structural on purpose: the caller
 * passes real `ReportScheduleRun` resources and gets them back unchanged, while
 * tests (and any future caller) can use plain objects.
 */
export interface ScheduledExportRunLike {
  reportScheduleId: string;
  reportName: string;
}

/** One schedule and the runs it produced, for the grouped exports view. */
export interface ScheduledExportGroup<T extends ScheduledExportRunLike> {
  /** `reportScheduleId` the runs share — stable across report renames. */
  reportScheduleId: string;
  /**
   * Display name taken from the group's FIRST run in input order (the newest,
   * given a `-ran-at` sort). A report renamed between runs therefore shows its
   * current name rather than whatever it was called historically.
   */
  reportName: string;
  /** The schedule's runs, in the order they arrived (newest first). */
  runs: T[];
}

/**
 * Groups scheduled-export runs by their schedule, for the "schedule → its runs"
 * partner view. Pure and order-preserving: groups come out ordered by their
 * newest run and runs keep their incoming order, so a caller that sorted runs
 * newest-first gets schedules newest-activity-first for free — no date parsing
 * and no second request, since every run already carries `reportScheduleId`.
 *
 * Runs missing a `reportScheduleId` are dropped: they cannot be attributed to a
 * schedule, and the endpoint only ever returns scheduled runs (a manual export
 * never creates a `ReportScheduleRun`), so this is a defensive no-op in practice.
 */
export function groupRunsBySchedule<T extends ScheduledExportRunLike>(
  runs: T[]
): ScheduledExportGroup<T>[] {
  const groups = new Map<string, ScheduledExportGroup<T>>();

  for (const run of runs) {
    if (!run.reportScheduleId) continue;

    const existing = groups.get(run.reportScheduleId);

    if (existing) {
      existing.runs.push(run);
    } else {
      groups.set(run.reportScheduleId, {
        reportScheduleId: run.reportScheduleId,
        reportName: run.reportName,
        runs: [run],
      });
    }
  }

  return [...groups.values()];
}
